import authSession from "../../lib/authSession";
import { API_URL } from "../../constants";
import { safeFetch, explainApiError } from "../../utils/toDisplayString";

export const resourcePersonEarningsInitialState = {
  earningsSummary: null,
  earningsAnalytics: null,
  earningsPending: null,
  earningsAvailable: null,
  earningsReserved: null,
  earningsLedger: [],
  earningsLedgerTotal: 0,
  earningsLedgerPages: 1,
  earningsHistory: [],
  earningsHistoryTotal: 0,
  earningsHistoryPages: 1,
  earningsLoading: false,
  earningsError: null,
};

function buildUrl(path, params = {}) {
  const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value !== undefined && value !== "")).toString();
  return `${API_URL.replace(/\/$/, "")}${path}${query ? `?${query}` : ""}`;
}

async function request(path, params) {
  if (!API_URL) throw new Error("REACT_APP_API_BASE_URL is not configured.");
  const token = authSession.getToken();
  const response = await safeFetch(buildUrl(path, params), {
    headers: { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: "omit",
  });
  const text = await response.text();
  let data = {};
  if (text) {
    try { data = JSON.parse(text); } catch { throw new Error(`Resource person earnings API returned a non-JSON response (${response.status}).`); }
  }
  if (!response.ok || data.error || data.success === false) throw new Error(explainApiError(response.status, data || {}, `Request failed with status ${response.status}.`));
  return data?.data && typeof data.data === "object" ? data.data : data;
}

function listResult(data) {
  const items = Array.isArray(data) ? data : data?.items || data?.entries || data?.transactions || data?.ledger || data?.history || [];
  return { items, total: data?.total ?? data?.totalItems ?? items.length, pages: data?.totalPages ?? data?.pages ?? 1 };
}

export function createResourcePersonEarningsActions(set) {
  const load = async (name, path, params) => {
    try { return { success: true, data: await request(path, params) }; }
    catch (error) { return { success: false, error: error.message, name }; }
  };

  return {
    fetchEarningsSummary: async () => { const result = await load("summary", "/api/resource-person/earnings/summary"); if (result.success) set({ earningsSummary: result.data }); return result; },
    fetchEarningsAnalytics: async () => { const result = await load("analytics", "/api/resource-person/earnings/analytics"); if (result.success) set({ earningsAnalytics: result.data }); return result; },
    fetchEarningsPending: async () => { const result = await load("pending", "/api/resource-person/earnings/pending"); if (result.success) set({ earningsPending: result.data }); return result; },
    fetchEarningsAvailable: async () => { const result = await load("available", "/api/resource-person/earnings/available"); if (result.success) set({ earningsAvailable: result.data }); return result; },
    fetchEarningsReserved: async () => { const result = await load("reserved", "/api/resource-person/earnings/reserved"); if (result.success) set({ earningsReserved: result.data }); return result; },
    fetchEarningsLedger: async (params = {}) => { const result = await load("ledger", "/api/resource-person/earnings/ledger", params); if (result.success) { const page = listResult(result.data); set({ earningsLedger: page.items, earningsLedgerTotal: page.total, earningsLedgerPages: page.pages }); } return result; },
    fetchEarningsHistory: async (params = {}) => { const result = await load("history", "/api/resource-person/earnings/history", params); if (result.success) { const page = listResult(result.data); set({ earningsHistory: page.items, earningsHistoryTotal: page.total, earningsHistoryPages: page.pages }); } return result; },
    fetchAllEarnings: async (params = {}) => {
      set({ earningsLoading: true, earningsError: null });
      const results = await Promise.all([
        load("summary", "/api/resource-person/earnings/summary"),
        load("analytics", "/api/resource-person/earnings/analytics"),
        load("pending", "/api/resource-person/earnings/pending"),
        load("available", "/api/resource-person/earnings/available"),
        load("reserved", "/api/resource-person/earnings/reserved"),
        load("ledger", "/api/resource-person/earnings/ledger", { page: 1, limit: 10, ...params.ledger }),
        load("history", "/api/resource-person/earnings/history", { page: 1, limit: 10, ...params.history }),
      ]);
      const [summary, analytics, pending, available, reserved, ledger, history] = results;
      const nextState = { earningsLoading: false };
      if (summary.success) nextState.earningsSummary = summary.data;
      if (analytics.success) nextState.earningsAnalytics = analytics.data;
      if (pending.success) nextState.earningsPending = pending.data;
      if (available.success) nextState.earningsAvailable = available.data;
      if (reserved.success) nextState.earningsReserved = reserved.data;
      if (ledger.success) { const page = listResult(ledger.data); nextState.earningsLedger = page.items; nextState.earningsLedgerTotal = page.total; nextState.earningsLedgerPages = page.pages; }
      if (history.success) { const page = listResult(history.data); nextState.earningsHistory = page.items; nextState.earningsHistoryTotal = page.total; nextState.earningsHistoryPages = page.pages; }
      const failed = results.find((result) => !result.success);
      if (failed) nextState.earningsError = failed.error;
      set(nextState);
      return { success: !failed, results, error: failed?.error };
    },
  };
}
